import Image from "next/image"
import Link from "@/components/ui/intent-link"
import { FooterDownloadButton } from "./FooterDownloadButton"

// Link to the main resource hubs here; each hub owns its detailed guides.
// This keeps navigation useful without repeating every keyword page sitewide.
const FOOTER_GROUPS = [
  {
    title: "Product",
    links: [
      { href: "/blogs/ai-interview-assistant", label: "AI Interview Assistant" },
      { href: "/interview-copilot", label: "Interview Copilot" },
      { href: "/coding-copilot", label: "Coding Interview Assistant" },
      { href: "/mock-interview", label: "Mock Interview Practice" },
      { href: "/pricing", label: "Live Assistant Pricing" },
      { href: "/mock-interview/pricing", label: "Practice Interview Pricing" },
      { href: "/download", label: "Download for Mac & Windows" },
      { href: "/#earn-with-us", label: "Earn with Us" },
    ],
  },
  {
    title: "Compare",
    links: [
      {
        href: "/blogs/best-ai-interview-assistant",
        label: "Best AI Interview Assistant Software",
      },
      { href: "/best-affordable-ai-interview-assistants-2026", label: "Affordable AI Assistants" },
      { href: "/vs", label: "All Tool Comparisons" },
      { href: "/alternative", label: "AI Interview Tool Alternatives" },
    ],
  },
  {
    title: "Resources",
    links: [
      { href: "/interviews", label: "Real Interview Question Library" },
      { href: "/interview-questions", label: "Interview Questions by Role & Company" },
      { href: "/interview-questions/sql", label: "SQL Interview Questions" },
      { href: "/ai-for-job-interviews", label: "AI for Job Seekers" },
      { href: "/interview-types", label: "Interview Types" },
      { href: "/coding-interviews", label: "Coding Interview Patterns" },
      { href: "/leetcode-problems", label: "LeetCode Practice Problems" },
      { href: "/blogs", label: "Blog" },
      { href: "/resume-templates", label: "Resume Templates" },
      { href: "/software-engineer-salaries", label: "Salary Guide" },
      { href: "/jobs", label: "Find Jobs" },
    ],
  },
  {
    title: "Help",
    links: [
      { href: "/contact", label: "Contact Us" },
      { href: "/help", label: "Help Center" },
      { href: "/instant-support", label: "Instant Support" },
      { href: "/faq", label: "Frequently Asked Questions" },
      { href: "/feedback", label: "Feedback" },
    ],
  },
  {
    title: "Legal",
    links: [
      { href: "/refund-policy", label: "Refund Policy" },
      { href: "/terms-and-conditions", label: "Terms of Service" },
      { href: "/cancellation-policy", label: "Cancellation Policy" },
      { href: "/privacy-policy", label: "Privacy Policy" },
      { href: "/policies", label: "All Policies" },
    ],
  },
] as const

const RELATED_PRODUCTS = [
  { href: "https://resumewizard.co", label: "ResumeWizard — AI Resume Builder" },
  { href: "https://senddart.com", label: "SendDart — Email Campaigns" },
  { href: "https://rankwin.co", label: "RankWin — Website Growth & SEO" },
  { href: "https://dmnow.co", label: "DM Now — Instagram DM Automation" },
  { href: "https://postcopilot.co", label: "PostCopilot — AI Social Media" },
] as const

const linkClassName =
  "text-sm text-gray-600 transition-colors hover:text-purple-600 dark:text-gray-400 dark:hover:text-purple-400"

export function Footer() {
  return (
    <footer className="border-t border-gray-200 backdrop-blur-sm dark:border-neutral-800">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3 xl:grid-cols-6">
          <div className="col-span-2 flex flex-col gap-5 sm:col-span-3 xl:col-span-1">
            <Link href="/" className="flex items-center gap-3">
              <Image
                src="/logo.svg"
                alt=""
                width={24}
                height={24}
                className="h-6 w-6 rounded-full"
              />
              <span className="bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-lg font-bold text-transparent">
                PhantomCodeAI
              </span>
            </Link>
            <p className="max-w-sm text-sm leading-relaxed text-gray-600 dark:text-gray-400">
              AI interview assistance and mock practice for developers, analysts, and job seekers.
              Prepare for coding, SQL, system design, and behavioral rounds.
            </p>
            <Link href="/#features" className={linkClassName}>
              Explore Features
            </Link>
            <FooterDownloadButton />
            <a
              href="https://startupfa.me/s/phantomcodeai?utm_source=www.phantomcodeai.com"
              target="_blank"
              rel="noopener"
              aria-label="PhantomCodeAI on Startup Fame"
              className="w-fit"
            >
              <Image
                src="https://startupfa.me/badge?t=classic&theme=default"
                alt="PhantomCodeAI - Featured on Startup Fame"
                width={171}
                height={54}
                unoptimized
              />
            </a>
          </div>

          {FOOTER_GROUPS.map((group) => (
            <nav key={group.title} aria-label={`Footer ${group.title}`}>
              <h2 className="mb-4 text-sm font-semibold text-purple-600 dark:text-purple-400">
                {group.title}
              </h2>
              <ul className="flex flex-col gap-2.5">
                {group.links.map((link) => (
                  <li key={link.href}>
                    <Link prefetch={link.href.startsWith("/blogs/") ? false : undefined} href={link.href} className={linkClassName}>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <nav
          aria-label="More from us"
          className="mt-10 border-t border-gray-200 pt-6 dark:border-neutral-800"
        >
          <h2 className="mb-3 text-sm font-semibold text-purple-600 dark:text-purple-400">
            More From Us
          </h2>
          <ul className="flex flex-wrap gap-x-6 gap-y-3">
            {RELATED_PRODUCTS.map((product) => (
              <li key={product.href}>
                <a href={product.href} target="_blank" rel="noopener" className={linkClassName}>
                  {product.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        <div className="mt-8 border-t border-gray-200 pt-6 dark:border-neutral-800">
          <p className="text-sm text-gray-600 dark:text-neutral-400" suppressHydrationWarning>
            © {new Date().getFullYear()} PhantomCodeAI. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  )
}
